// src/pages/nations/EliminateDialog.tsx

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import type { Nation, Phase, EliminateNationRequest } from "@/types/api.types";

interface EliminateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nation: Nation | null;
  onConfirm: (data: EliminateNationRequest) => void;
  isEliminating: boolean;
}

const PHASES: { value: Phase; label: string }[] = [
  { value: "GROUP_STAGE", label: "Group Stage" },
  { value: "ROUND_OF_32", label: "Round of 32" },
  { value: "ROUND_OF_16", label: "Round of 16" },
  { value: "QUARTER_FINAL", label: "Quarter Final" },
  { value: "SEMI_FINAL", label: "Semi Final" },
  { value: "THIRD_PLACE", label: "Third Place Match" },
  { value: "FINAL", label: "Final" },
];

export default function EliminateDialog({
  open,
  onOpenChange,
  nation,
  onConfirm,
  isEliminating,
}: EliminateDialogProps) {
  const [selectedPhase, setSelectedPhase] = useState<Phase>("GROUP_STAGE");

  if (!nation) return null;

  const handleConfirm = () => {
    onConfirm({ phase: selectedPhase });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Eliminate Nation
          </DialogTitle>
          <DialogDescription>
            This action will deactivate all players from {nation.name}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>⚠️ Warning:</strong> This will:
            </p>
            <ul className="text-sm text-yellow-800 mt-2 ml-4 list-disc">
              <li>Mark {nation.name} as eliminated</li>
              <li>Deactivate all {nation._count?.players || 0} players</li>
              <li>Prevent future player selections from this nation</li>
              <li>This action cannot be easily reversed</li>
            </ul>
          </div>

          <div>
            <Label htmlFor="phase" className="text-gray-700 mb-2">
              Elimination Phase <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedPhase}
              onValueChange={(value: string) => setSelectedPhase(value as Phase)}
            >
              <SelectTrigger className="bg-white border-gray-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PHASES.map((phase) => (
                  <SelectItem key={phase.value} value={phase.value}>
                    {phase.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Select the tournament phase when this nation was eliminated
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isEliminating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={isEliminating}
            className="bg-red-600 hover:bg-red-700"
          >
            {isEliminating ? "Eliminating..." : "Eliminate Nation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
